import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaClock, FaTools, FaUsers, FaAward, FaHandshake, FaPhone } from 'react-icons/fa';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <FaShieldAlt />,
      title: 'Reliability',
      description: 'Count on us to be there when you need us most, with dependable service every time.'
    },
    {
      icon: <FaClock />,
      title: '24/7 Availability',
      description: 'Emergencies don\'t wait, and neither do we. We\'re available around the clock.'
    },
    {
      icon: <FaTools />,
      title: 'Professional Service',
      description: 'Our trained technicians use the latest equipment to ensure quality service.'
    },
    {
      icon: <FaUsers />,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We treat every customer with care and respect.'
    },
    {
      icon: <FaAward />,
      title: 'Excellence',
      description: 'We strive for excellence in every service we provide to our valued customers.'
    },
    {
      icon: <FaHandshake />,
      title: 'Trust',
      description: 'We build lasting relationships through honest, transparent, and fair service.'
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="page-title">About Toronto Roadside Assistance</h1>
            <p className="page-subtitle">
              Your trusted partner for professional towing and roadside assistance services in Toronto
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="section about-content-section">
        <div className="container">
          <div className="about-content-grid">
            <motion.div
              className="about-main-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="content-title">Who We Are</h2>
              <p className="content-text">
                Toronto Roadside Assistance is a professional towing company located in Toronto, 
                where we pride ourselves on providing top-quality and reliable towing services 
                to all our valued customers. Our team of highly skilled and experienced professionals 
                is dedicated to ensuring that all of our clients receive the best possible assistance 
                whenever they require our services.
              </p>
              <p className="content-text">
                At Toronto Roadside Assistance, we offer a comprehensive range of services, including 
                towing, lockout, battery boost, and emergency tire changing services. Our towing service 
                is available 24/7, so no matter what time of day or night you need us, we will be there 
                to assist you promptly.
              </p>
              <p className="content-text">
                We understand that emergencies can happen at any time, which is why we offer a range 
                of services designed to get you back on the road as quickly as possible. Whether you 
                are locked out of your car, have a flat tire, or require a battery boost, our team of 
                experts is ready to assist you.
              </p>
            </motion.div>

            <motion.div
              className="about-highlight-box"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="highlight-content">
                <FaPhone className="highlight-icon" />
                <h3 className="highlight-title">Need Help Now?</h3>
                <p className="highlight-text">
                  Our team is available 24/7 to provide immediate assistance for any roadside emergency.
                </p>
                <a href="tel:6478988242" className="btn btn-primary">
                  Call Us: (647) 898-8242
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="section section-dark commitment-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Commitment to You</h2>
            <p className="section-subtitle">
              Quality service, expert care, and customer satisfaction are at the heart of everything we do
            </p>
          </motion.div>

          <div className="commitment-content">
            <motion.div
              className="commitment-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p>
                Our team of trained technicians is equipped with the latest tools and equipment to 
                ensure that your vehicle is towed safely and securely, without any damage. We take 
                great care to ensure that all our services are carried out efficiently, and with the 
                utmost care and attention to detail.
              </p>
              <p>
                At Toronto Roadside Assistance, we are committed to providing our customers with the 
                best possible service at an affordable price. We understand that being stuck on the 
                side of the road can be stressful, which is why we aim to provide a fast and reliable 
                service to all our clients.
              </p>
              <p>
                So, if you are looking for a reliable and professional towing company in Toronto that 
                provides towing, lockout, battery boost, and emergency tire changing services, then 
                look no further than our team of experts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section values-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide every service we provide
            </p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-dark cta-about-section">
        <div className="container">
          <motion.div
            className="cta-about-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-about-title">Ready to Experience Professional Service?</h2>
            <p className="cta-about-text">
              Contact us today for more information on how we can assist you with all your roadside needs.
            </p>
            <div className="cta-about-buttons">
              <a href="tel:6478988242" className="btn btn-primary btn-large">
                Call Now
              </a>
              <Link to="/contact" className="btn btn-secondary btn-large">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;

